import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUserRoleContext } from '@/contexts/UserRoleContext';

export interface UserProfile {
  id: string;
  user_id: string;
  company_name?: string | null;
  farm_hectares?: number | null;
  crop_type?: string | null;
  phone?: string | null;
  email?: string | null;
  full_name?: string | null;
  country?: string | null;
  urban_management_type?: string | null;
  managed_green_areas?: number | null;
  created_at?: string;
  updated_at?: string;
  iban?: string | null;
  water_cost_per_cubic_meter?: number | null;
  is_cooperative?: boolean | null;
  cooperative_id?: string | null;
}

export type UserRole = 'farmer' | 'insurer' | 'urban_manager' | 'admin' | 'pilot_user' | 'cooperative';

export const useUserRole = () => {
  // Try context first (when inside UserRoleProvider)
  const ctx = useUserRoleContext();

  // Fallback state for when used outside provider (shouldn't happen but safe)
  const [fallbackProfile, setFallbackProfile] = useState<UserProfile | null>(null);
  const [fallbackRole, setFallbackRole] = useState<UserRole | null>(null);
  const [fallbackLoading, setFallbackLoading] = useState(true);
  const [fallbackError, setFallbackError] = useState<string | null>(null);

  useEffect(() => {
    if (ctx) return; // Context available, skip fallback

    let mounted = true;
    const fetchProfileAndRole = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session?.user) {
          if (mounted) { setFallbackProfile(null); setFallbackRole(null); setFallbackLoading(false); }
          return;
        }
        const user = session.user;
        const [profileResult, roleResult] = await Promise.all([
          supabase.from('profiles').select('*').eq('user_id', user.id).single(),
          supabase.from('user_roles').select('role').eq('user_id', user.id).order('created_at', { ascending: true }).limit(1)
        ]);
        if (profileResult.error) throw profileResult.error;
        let role: UserRole = 'farmer';
        if (roleResult.data && roleResult.data.length > 0) {
          role = roleResult.data[0].role as UserRole;
        } else if ((profileResult.data as any)?.role) {
          role = (profileResult.data as any).role as UserRole;
        }
        if (mounted) { setFallbackRole(role); setFallbackProfile(profileResult.data); setFallbackLoading(false); }
      } catch (err) {
        if (mounted) { setFallbackError(err instanceof Error ? err.message : 'Failed to fetch profile'); setFallbackLoading(false); }
      }
    };
    fetchProfileAndRole();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      if (mounted) { setFallbackLoading(true); fetchProfileAndRole(); }
    });
    return () => { mounted = false; subscription.unsubscribe(); };
  }, [ctx]);

  const profile = ctx?.profile ?? fallbackProfile;
  const userRole = ctx?.userRole ?? fallbackRole;
  const loading = ctx ? ctx.loading : fallbackLoading;
  const error = ctx ? ctx.error : fallbackError;

  const isInsurer = userRole === 'insurer';
  const isFarmer = userRole === 'farmer';
  const isAdmin = userRole === 'admin';
  const isUrbanManager = userRole === 'urban_manager';
  const isMunicipal = profile?.urban_management_type === 'municipal';
  const isCooperative = profile?.is_cooperative === true;
  const isMemberOfCooperative = !!profile?.cooperative_id;

  return {
    profile,
    userRole,
    loading,
    error,
    isInsurer,
    isFarmer,
    isAdmin,
    isUrbanManager,
    isMunicipal,
    isCooperative,
    isMemberOfCooperative
  };
};
